import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Save,
  Loader2,
  Package,
  DollarSign,
  Layers,
  Image as ImageIcon,
  Search as SearchIcon,
  Settings,
  Upload,
  Trash2,
  Plus,
  GripVertical,
  FolderPlus,
  FileText,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { api, API_BASE } from '@/lib/api';
import type { Product, Category, Store, ProductVariant } from '../types';

interface ProductFormProps {
  product?: Product | null;
  stores: Store[];
  categories: Category[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Product>) => Promise<void>;
  isSaving: boolean;
  defaultStoreId?: string; // Used to pre-select store when creating new product
}

type TabId = 'basic' | 'pricing' | 'inventory' | 'media' | 'details' | 'seo' | 'variants';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'basic', label: 'Basic', icon: Package },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'inventory', label: 'Inventory', icon: Layers },
  { id: 'media', label: 'Media', icon: ImageIcon },
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'seo', label: 'SEO', icon: SearchIcon },
  { id: 'variants', label: 'Variants', icon: Settings },
];

const PRODUCT_TYPES = [
  { value: 'PHYSICAL', label: 'Physical Product', description: 'Shipped to customer' },
  { value: 'DIGITAL', label: 'Digital Product', description: 'Downloadable file' },
  { value: 'SERVICE', label: 'Service', description: 'Non-physical service' },
  { value: 'EXTERNAL', label: 'External', description: 'Link to external site' },
];

export function ProductForm({
  product,
  stores,
  categories,
  isOpen,
  onClose,
  onSave,
  isSaving,
  defaultStoreId,
}: ProductFormProps) {
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [form, setForm] = useState<Partial<Product>>({});
  const [variants, setVariants] = useState<Partial<ProductVariant>[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Specifications state (key-value pairs)
  const [specs, setSpecs] = useState<{ key: string; value: string }[]>([]);
  
  // Category creation state
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const queryClient = useQueryClient();
  
  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string; storeId?: string }) => 
      api.post<{ category: Category }>('/products/categories', data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewCategoryName('');
      setShowNewCategory(false);
      toast.success('Category created');
      // Auto-select the new category
      if (result?.category?.id) {
        setForm(prev => ({ ...prev, categoryId: result.category.id }));
      }
    },
    onError: (error: any) => {
      toast.error(error.data?.error || 'Failed to create category');
    },
  });

  // Initialize form when product changes
  useEffect(() => {
    if (product) {
      setForm({
        storeId: product.storeId,
        categoryId: product.categoryId,
        name: product.name,
        description: product.description,
        code: product.code,
        price: product.price,
        currency: product.currency || 'EUR',
        compareAtPrice: product.compareAtPrice,
        costPrice: product.costPrice,
        quantity: product.quantity,
        trackInventory: product.trackInventory,
        lowStockThreshold: product.lowStockThreshold,
        allowBackorder: product.allowBackorder,
        categoryName: product.categoryName,
        subcategory: product.subcategory,
        tags: product.tags || [],
        imageUrl: product.imageUrl,
        imageUrls: product.imageUrls || [],
        productType: product.productType,
        externalUrl: product.externalUrl,
        downloadUrl: product.downloadUrl,
        weight: product.weight,
        isActive: product.isActive,
        isFeatured: product.isFeatured,
        slug: product.slug,
        metaTitle: product.metaTitle,
        metaDescription: product.metaDescription,
        // Product details
        usageInstructions: product.usageInstructions,
        storageConditions: product.storageConditions,
        warnings: product.warnings,
        ingredients: product.ingredients,
      });
      setVariants(product.variants || []);
      // Convert specifications object to array
      if (product.specifications && typeof product.specifications === 'object') {
        setSpecs(Object.entries(product.specifications).map(([key, value]) => ({ key, value: String(value) })));
      } else {
        setSpecs([]);
      }
    } else {
      setForm({
        storeId: defaultStoreId, // Use default store if provided
        currency: 'EUR',
        quantity: 0,
        trackInventory: true,
        lowStockThreshold: 5,
        allowBackorder: false,
        productType: 'PHYSICAL',
        isActive: true,
        isFeatured: false,
        tags: [],
        imageUrls: [],
      });
      setVariants([]);
      setSpecs([]);
    }
    setActiveTab('basic');
  }, [product, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || form.price === undefined) {
      toast.error('Name and price are required');
      return;
    }
    // Convert specs array to specifications object
    const specifications = specs.reduce((acc, { key, value }) => {
      if (key.trim()) {
        acc[key.trim()] = value;
      }
      return acc;
    }, {} as Record<string, string>);
    
    await onSave({
      ...form,
      specifications: Object.keys(specifications).length > 0 ? specifications : undefined,
    });
  };

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { accessToken } = useAuthStore.getState();
      const response = await fetch(`${API_BASE}/products/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Upload failed');
      }

      const data = await response.json();
      
      // Set as main image if none exists, otherwise add to gallery
      if (!form.imageUrl) {
        setForm((prev) => ({ ...prev, imageUrl: data.url }));
      } else {
        setForm((prev) => ({
          ...prev,
          imageUrls: [...(prev.imageUrls || []), data.url],
        }));
      }
      
      toast.success('Image uploaded!');
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageUpload(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const removeImage = (url: string) => {
    if (url === form.imageUrl) {
      // If removing main image, promote first gallery image
      const gallery = form.imageUrls || [];
      setForm((prev) => ({
        ...prev,
        imageUrl: gallery[0] || undefined,
        imageUrls: gallery.slice(1),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        imageUrls: (prev.imageUrls || []).filter((u) => u !== url),
      }));
    }
  };

  const setAsMainImage = (url: string) => {
    const currentMain = form.imageUrl;
    const gallery = (form.imageUrls || []).filter((u) => u !== url);
    if (currentMain) gallery.unshift(currentMain);
    setForm((prev) => ({
      ...prev,
      imageUrl: url,
      imageUrls: gallery,
    }));
  };

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        name: '',
        price: form.price || 0,
        quantity: 0,
        isDefault: prev.length === 0,
        isActive: true,
        sortOrder: prev.length,
      },
    ]);
  };

  const updateVariant = (index: number, data: Partial<ProductVariant>) => {
    setVariants((prev) =>
      prev.map((v, i) => (i === index ? { ...v, ...data } : v))
    );
  };

  const removeVariant = (index: number) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  const allImages = [form.imageUrl, ...(form.imageUrls || [])].filter(Boolean) as string[];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end lg:items-center justify-center lg:p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: '100%' }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white w-full max-lg:h-[95vh] max-lg:rounded-t-2xl lg:rounded-2xl lg:max-w-4xl lg:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 lg:px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            {product ? 'Edit Product' : 'New Product'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="flex border-b border-gray-200 overflow-x-auto bg-gray-50 no-scrollbar">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-1.5 px-3 lg:px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px shrink-0',
                activeTab === tab.id
                  ? 'text-orange-600 border-orange-500 bg-white'
                  : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-gray-100'
              )}
            >
              <tab.icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 lg:py-6 bg-gray-50">
          <AnimatePresence mode="wait">
            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <motion.div
                key="basic"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Store
                    </label>
                    <select
                      value={form.storeId || ''}
                      onChange={(e) => setForm({ ...form, storeId: e.target.value || undefined })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    >
                      <option value="">No store</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-slate-700">
                        Category
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowNewCategory(!showNewCategory)}
                        className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                      >
                        <FolderPlus size={14} />
                        New
                      </button>
                    </div>
                    
                    {showNewCategory ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Category name"
                          className="w-full bg-white border border-orange-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newCategoryName.trim()) {
                              e.preventDefault();
                              createCategoryMutation.mutate({ 
                                name: newCategoryName.trim(),
                                storeId: form.storeId 
                              });
                            }
                            if (e.key === 'Escape') {
                              setShowNewCategory(false);
                              setNewCategoryName('');
                            }
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (newCategoryName.trim()) {
                                createCategoryMutation.mutate({ 
                                  name: newCategoryName.trim(),
                                  storeId: form.storeId 
                                });
                              }
                            }}
                            disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs py-1.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
                          >
                            {createCategoryMutation.isPending ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              'Create'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowNewCategory(false);
                              setNewCategoryName('');
                            }}
                            className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 border border-gray-300 rounded-lg"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <select
                        value={form.categoryId || ''}
                        onChange={(e) => setForm({ ...form, categoryId: e.target.value || undefined })}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      >
                        <option value="">No category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.parent ? `${cat.parent.name} > ` : ''}{cat.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={form.name || ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="e.g. Premium Cannabis Flower"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 min-h-[100px] focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Describe your product..."
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      SKU / Code
                    </label>
                    <input
                      type="text"
                      value={form.code || ''}
                      onChange={(e) => setForm({ ...form, code: e.target.value })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 font-mono focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      placeholder="SKU-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Product Type
                    </label>
                    <select
                      value={form.productType || 'PHYSICAL'}
                      onChange={(e) => setForm({ ...form, productType: e.target.value as any })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    >
                      {PRODUCT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive ?? true}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isFeatured ?? false}
                      onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700">Featured</span>
                  </label>
                </div>
              </motion.div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <motion.div
                key="pricing"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Price *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                        {form.currency === 'EUR' ? '€' : form.currency}
                      </span>
                      <input
                        type="number"
                        value={form.price || ''}
                        onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-white border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={form.currency || 'EUR'}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="THB">THB (฿)</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Compare at Price
                      <span className="text-xs text-slate-500 ml-1">(Original price for sale)</span>
                    </label>
                    <input
                      type="number"
                      value={form.compareAtPrice || ''}
                      onChange={(e) => setForm({ ...form, compareAtPrice: parseFloat(e.target.value) || undefined })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Cost Price
                      <span className="text-xs text-slate-500 ml-1">(For profit calculation)</span>
                    </label>
                    <input
                      type="number"
                      value={form.costPrice || ''}
                      onChange={(e) => setForm({ ...form, costPrice: parseFloat(e.target.value) || undefined })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                {form.price && form.costPrice && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-slate-700">
                      Profit Margin:{' '}
                      <span className="text-green-600 font-semibold">
                        {(((form.price - form.costPrice) / form.price) * 100).toFixed(1)}%
                      </span>
                      {' · '}
                      Profit:{' '}
                      <span className="text-green-600 font-semibold">
                        €{(form.price - form.costPrice).toFixed(2)}
                      </span>
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
              <motion.div
                key="inventory"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.trackInventory ?? true}
                      onChange={(e) => setForm({ ...form, trackInventory: e.target.checked })}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700">Track inventory</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.allowBackorder ?? false}
                      onChange={(e) => setForm({ ...form, allowBackorder: e.target.checked })}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-700">Allow backorders</span>
                  </label>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Quantity in Stock
                    </label>
                    <input
                      type="number"
                      value={form.quantity ?? 0}
                      onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Low Stock Threshold
                    </label>
                    <input
                      type="number"
                      value={form.lowStockThreshold ?? 5}
                      onChange={(e) => setForm({ ...form, lowStockThreshold: parseInt(e.target.value) || 0 })}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      min="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      You'll be notified when stock falls below this
                    </p>
                  </div>
                </div>

                {form.productType === 'PHYSICAL' && (
                  <>
                    <h4 className="text-sm font-medium text-slate-700 mt-6 mb-2">
                      Shipping Dimensions
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Weight (kg)</label>
                        <input
                          type="number"
                          value={form.weight || ''}
                          onChange={(e) => setForm({ ...form, weight: parseFloat(e.target.value) || undefined })}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          step="0.001"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Length (cm)</label>
                        <input
                          type="number"
                          value={(form as any).length || ''}
                          onChange={(e) => setForm({ ...form, length: parseFloat(e.target.value) || undefined } as any)}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Width (cm)</label>
                        <input
                          type="number"
                          value={(form as any).width || ''}
                          onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) || undefined } as any)}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Height (cm)</label>
                        <input
                          type="number"
                          value={(form as any).height || ''}
                          onChange={(e) => setForm({ ...form, height: parseFloat(e.target.value) || undefined } as any)}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          min="0"
                        />
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <motion.div
                key="media"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                {/* Upload Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={clsx(
                    'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all bg-white',
                    isDragging
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-300 hover:border-gray-400'
                  )}
                >
                  {uploadingImage ? (
                    <Loader2 className="mx-auto mb-2 animate-spin text-orange-500" size={48} />
                  ) : (
                    <Upload className="mx-auto mb-2 text-slate-400" size={48} />
                  )}
                  <p className="text-sm text-slate-600">
                    {uploadingImage
                      ? 'Uploading...'
                      : 'Drag & drop images or click to browse'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP up to 10MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                    e.target.value = '';
                  }}
                />

                {/* Image Gallery */}
                {allImages.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Product Images ({allImages.length})
                    </label>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {allImages.map((url, index) => (
                        <div
                          key={url}
                          className={clsx(
                            'relative group rounded-lg overflow-hidden border-2',
                            index === 0
                              ? 'border-orange-500'
                              : 'border-gray-200'
                          )}
                        >
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-full h-24 object-cover"
                          />
                          {index === 0 && (
                            <span className="absolute top-1 left-1 text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded">
                              Main
                            </span>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => setAsMainImage(url)}
                                className="p-1.5 bg-white/90 text-slate-700 rounded hover:bg-white"
                                title="Set as main"
                              >
                                <GripVertical size={16} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImage(url)}
                              className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                              title="Remove"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* URL Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Or paste image URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://..."
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    onBlur={(e) => {
                      if (e.target.value) {
                        if (!form.imageUrl) {
                          setForm((prev) => ({ ...prev, imageUrl: e.target.value }));
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            imageUrls: [...(prev.imageUrls || []), e.target.value],
                          }));
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </motion.div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                {/* Specifications */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-slate-700">Specifications</h4>
                      <p className="text-xs text-slate-500">
                        Add product characteristics (THC, CBD, country, etc.)
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSpecs([...specs, { key: '', value: '' }])}
                      className="btn btn-sm bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <Plus size={16} />
                      Add Spec
                    </button>
                  </div>
                  
                  {specs.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-slate-500">No specifications yet</p>
                      <p className="text-xs text-slate-400 mt-1">Click "Add Spec" to add product characteristics</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {specs.map((spec, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={spec.key}
                            onChange={(e) => {
                              const newSpecs = [...specs];
                              newSpecs[index].key = e.target.value;
                              setSpecs(newSpecs);
                            }}
                            placeholder="Key (e.g. THC)"
                            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          />
                          <input
                            type="text"
                            value={spec.value}
                            onChange={(e) => {
                              const newSpecs = [...specs];
                              newSpecs[index].value = e.target.value;
                              setSpecs(newSpecs);
                            }}
                            placeholder="Value (e.g. 20%)"
                            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                          />
                          <button
                            type="button"
                            onClick={() => setSpecs(specs.filter((_, i) => i !== index))}
                            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Usage Instructions */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Usage Instructions
                  </label>
                  <textarea
                    value={form.usageInstructions || ''}
                    onChange={(e) => setForm({ ...form, usageInstructions: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="How to use this product..."
                    rows={3}
                  />
                </div>

                {/* Ingredients */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Ingredients / Composition
                  </label>
                  <textarea
                    value={form.ingredients || ''}
                    onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Product composition or ingredients..."
                    rows={3}
                  />
                </div>

                {/* Storage Conditions */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Storage Conditions
                  </label>
                  <textarea
                    value={form.storageConditions || ''}
                    onChange={(e) => setForm({ ...form, storageConditions: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="How to store this product..."
                    rows={2}
                  />
                </div>

                {/* Warnings */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Warnings / Contraindications
                  </label>
                  <textarea
                    value={form.warnings || ''}
                    onChange={(e) => setForm({ ...form, warnings: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Important warnings or contraindications..."
                    rows={2}
                  />
                </div>
              </motion.div>
            )}

            {/* SEO Tab */}
            {activeTab === 'seo' && (
              <motion.div
                key="seo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    URL Slug
                  </label>
                  <input
                    type="text"
                    value={form.slug || ''}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 font-mono focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="premium-cannabis-flower"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Leave empty to auto-generate from name
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    value={form.metaTitle || ''}
                    onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Premium Cannabis Flower | Your Store"
                    maxLength={60}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {form.metaTitle?.length || 0}/60 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Meta Description
                  </label>
                  <textarea
                    value={form.metaDescription || ''}
                    onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="High-quality cannabis flower with premium terpene profiles..."
                    rows={3}
                    maxLength={160}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {form.metaDescription?.length || 0}/160 characters
                  </p>
                </div>
              </motion.div>
            )}

            {/* Variants Tab */}
            {activeTab === 'variants' && (
              <motion.div
                key="variants"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700">Product Variants</h4>
                    <p className="text-xs text-slate-500">
                      Add size, weight, or color variations
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="btn btn-sm bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus size={16} />
                    Add Variant
                  </button>
                </div>

                {variants.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Settings size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No variants yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {variants.map((variant, index) => (
                      <div
                        key={index}
                        className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
                      >
                        <div className="grid md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">Name *</label>
                            <input
                              type="text"
                              value={variant.name || ''}
                              onChange={(e) => updateVariant(index, { name: e.target.value })}
                              className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1.5 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                              placeholder="e.g. 100g"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">SKU</label>
                            <input
                              type="text"
                              value={variant.sku || ''}
                              onChange={(e) => updateVariant(index, { sku: e.target.value })}
                              className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1.5 text-slate-900 text-sm font-mono focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                              placeholder="SKU-001-100G"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">Price *</label>
                            <input
                              type="number"
                              value={variant.price || ''}
                              onChange={(e) => updateVariant(index, { price: parseFloat(e.target.value) || 0 })}
                              className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1.5 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                              step="0.01"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">Quantity</label>
                            <input
                              type="number"
                              value={variant.quantity || 0}
                              onChange={(e) => updateVariant(index, { quantity: parseInt(e.target.value) || 0 })}
                              className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1.5 text-slate-900 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={variant.isDefault || false}
                                onChange={(e) => updateVariant(index, { isDefault: e.target.checked })}
                                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                              />
                              Default
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={variant.isActive ?? true}
                                onChange={(e) => updateVariant(index, { isActive: e.target.checked })}
                                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                              />
                              Active
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1"
                          >
                            <Trash2 size={16} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* Footer - Sticky on mobile */}
        <div className="flex items-center justify-between lg:justify-end gap-3 px-4 lg:px-6 py-4 border-t border-gray-200 bg-white sticky bottom-0 safe-area-bottom">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 lg:flex-none px-4 py-3 lg:py-2 text-slate-600 hover:text-slate-900 hover:bg-gray-100 rounded-xl lg:rounded-lg transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !form.name || form.price === undefined}
            className="flex-1 lg:flex-none btn bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-not-allowed py-3 lg:py-2.5 rounded-xl lg:rounded-lg"
          >
            {isSaving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>{product ? 'Update' : 'Create'}</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
